import env from '@/env';

const BRAND_NAME = '블라썸 로고스';
const DEFAULT_WEDDING_OG_IMAGE = '/meta_bg_wedding.jpg';
const DEFAULT_FUNERAL_OG_IMAGE = '/meta_bg_funeral_ori2.jpg';

const toSafeText = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value).trim();
};

const pickFirstNonEmpty = (values = []) => {
  for (const value of values) {
    const normalized = toSafeText(value);

    if (normalized) {
      return normalized;
    }
  }

  return '';
};

const joinWithSpace = (values = []) => values
  .map((value) => toSafeText(value))
  .filter(Boolean)
  .join(' ');

const joinWithDivider = (values = []) => values
  .map((value) => toSafeText(value))
  .filter(Boolean)
  .join(' · ');

const getFirstAttachPath = (info) => {
  const attachList = Array.isArray(info?.attachList) ? info.attachList : [];
  return pickFirstNonEmpty(attachList);
};

const isAbsoluteUrl = (value) => /^(https?:)?\/\//i.test(value);

const toAbsoluteUrl = (value) => {
  const normalized = toSafeText(value);

  if (!normalized) {
    return '';
  }

  if (isAbsoluteUrl(normalized) || normalized.startsWith('data:')) {
    return normalized;
  }

  if (typeof window === 'undefined') {
    return normalized;
  }

  if (normalized.startsWith('/')) {
    return `${window.location.origin}${normalized}`;
  }

  return `${window.location.origin}/${normalized.replace(/^\/+/, '')}`;
};

const toAbsoluteImageUrl = (path) => {
  const normalized = toSafeText(path);

  if (!normalized) {
    return '';
  }

  if (normalized.startsWith('data:') || isAbsoluteUrl(normalized) || normalized.startsWith('/')) {
    return toAbsoluteUrl(normalized);
  }

  return `${env.api.SPRING_API_URL}/file/image?path=${encodeURIComponent(normalized)}`;
};

const buildBaseMeta = ({
  title,
  description,
  image,
  url,
}) => ({
  title,
  description,
  ogTitle: title,
  ogDescription: description,
  ogType: 'website',
  ogUrl: toAbsoluteUrl(url),
  ogImage: toAbsoluteImageUrl(image),
  twitterCard: 'summary_large_image',
  twitterTitle: title,
  twitterDescription: description,
  twitterImage: toAbsoluteImageUrl(image),
});

const buildFuneralMeta = (invitation, currentUrl) => {
  const funeralInfo = invitation?.funeralInfo || {};

  const deceasedName = toSafeText(funeralInfo.deceasedName);
  const funeralDateTime = joinWithSpace([funeralInfo.funeralDate, funeralInfo.funeralTime]);
  const funeralLocation = pickFirstNonEmpty([
    funeralInfo.funeralLocationName,
    funeralInfo.funeralLocationDtl,
  ]);

  const title = deceasedName
    ? `${deceasedName} 부고 | ${BRAND_NAME}`
    : `부고장 | ${BRAND_NAME}`;

  const description = joinWithDivider([funeralDateTime, funeralLocation]) || '마음을 전하는 온라인 부고장';

  const imagePath = pickFirstNonEmpty([
    getFirstAttachPath(funeralInfo),
    DEFAULT_FUNERAL_OG_IMAGE,
  ]);

  return buildBaseMeta({
    title,
    description,
    image: imagePath,
    url: currentUrl,
  });
};

const buildWeddingMeta = (invitation, currentUrl) => {
  const weddingInfo = invitation?.weddingInfo || {};

  const groomName = toSafeText(weddingInfo.groomName);
  const brideName = toSafeText(weddingInfo.brideName);
  const eventDateTime = joinWithSpace([weddingInfo.eventDate, weddingInfo.eventTime]);
  const eventLocationName = toSafeText(weddingInfo.eventLocationName);

  const title = groomName && brideName
    ? `${groomName} ♥ ${brideName} 청첩장 | ${BRAND_NAME}`
    : `청첩장 | ${BRAND_NAME}`;

  const description = joinWithDivider([eventDateTime, eventLocationName]) || '소중한 날에 초대합니다';

  const imagePath = pickFirstNonEmpty([
    weddingInfo.mainImgPath,
    getFirstAttachPath(weddingInfo),
    DEFAULT_WEDDING_OG_IMAGE,
  ]);

  return buildBaseMeta({
    title,
    description,
    image: imagePath,
    url: currentUrl,
  });
};

export const buildFallbackInvitationMeta = (currentUrl) => buildBaseMeta({
  title: `블라썸 로고스 초대장`,
  description: '소중한 마음을 전하는 온라인 초대장입니다.',
  image: DEFAULT_WEDDING_OG_IMAGE,
  url: currentUrl,
});

export const buildInvitationMeta = (invitation, currentUrl) => {
  const invitationType = toSafeText(invitation?.invitationType);

  if (invitationType === 'F') {
    return buildFuneralMeta(invitation, currentUrl);
  }

  if (invitationType === 'W') {
    return buildWeddingMeta(invitation, currentUrl);
  }

  return buildFallbackInvitationMeta(currentUrl);
};
